import React, { useEffect, useMemo, useRef, useState } from "react";
import { Home, TrendingUp } from "lucide-react";
import { dapi as api } from "./deskApi";

const fmt = (n) =>
  isFinite(n) ? n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }) : "—";
const fmt2 = (n) =>
  isFinite(n) ? n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }) : "—";
const num = (v) => (v === "" || v === null ? 0 : parseFloat(v) || 0);

function Field({ label, value, onChange, suffix, testid, step = "any" }) {
  return (
    <label className="block">
      <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">{label}</span>
      <span className="mt-0.5 flex items-center gap-1 border border-slate-300 rounded-md px-2 py-1.5 bg-white focus-within:ring-2 focus-within:ring-emerald-400">
        <input
          type="number"
          step={step}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full text-sm outline-none bg-transparent"
          data-testid={testid}
        />
        {suffix && <span className="text-xs text-slate-400 shrink-0">{suffix}</span>}
      </span>
    </label>
  );
}

function Stat({ label, value, big = false, testid }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{label}</p>
      <p className={`${big ? "text-2xl" : "text-lg"} font-bold text-slate-900 tabular-nums`} data-testid={testid}>{value}</p>
    </div>
  );
}

function MortgageCalc() {
  const [price, setPrice] = useState("400000");
  const [down, setDown] = useState("80000");
  const [years, setYears] = useState("30");
  const [rate, setRate] = useState("6.5");
  const [tax, setTax] = useState("6000");
  const [ins, setIns] = useState("1800");
  const [hoa, setHoa] = useState("0");
  const [live, setLive] = useState(null); // {rate30, rate15, as_of}
  const rateTouched = useRef(false);

  // Auto-detect the current U.S. average rate for the chosen term.
  useEffect(() => {
    api.get("/desktop/money/rates").then((r) => {
      if (r.data?.rate30) {
        setLive(r.data);
        if (!rateTouched.current) setRate(String(r.data.rate30));
      }
    }).catch(() => {});
  }, []);
  useEffect(() => {
    if (!live || rateTouched.current) return;
    const avg = num(years) <= 20 ? live.rate15 : live.rate30;
    if (avg) setRate(String(avg));
  }, [years, live]);

  const r = useMemo(() => {
    const loan = Math.max(0, num(price) - num(down));
    const mr = num(rate) / 100 / 12;
    const n = Math.max(1, num(years) * 12);
    const pi = mr > 0 ? (loan * mr) / (1 - Math.pow(1 + mr, -n)) : loan / n;
    const monthly = pi + num(tax) / 12 + num(ins) / 12 + num(hoa);
    return { loan, pi, monthly, totalInterest: pi * n - loan, totalPaid: pi * n };
  }, [price, down, years, rate, tax, ins, hoa]);

  const downPct = num(price) > 0 ? ((num(down) / num(price)) * 100).toFixed(1) : "0";

  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <div className="space-y-2.5">
        <Field label="Home price" value={price} onChange={setPrice} suffix="$" testid="mc-price" />
        <div>
          <Field label={`Down payment (${downPct}%)`} value={down} onChange={setDown} suffix="$" testid="mc-down" />
          <div className="flex gap-1 mt-1">
            {[5, 10, 20].map((p) => (
              <button key={p} type="button" onClick={() => setDown(String(Math.round(num(price) * p / 100)))}
                className="text-[11px] px-2 py-0.5 rounded border border-slate-200 hover:bg-emerald-50 hover:border-emerald-300" data-testid={`mc-down-${p}`}>
                {p}%
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          <Field label="Loan term" value={years} onChange={setYears} suffix="yrs" testid="mc-years" />
          <Field label="Interest rate" value={rate} onChange={(v) => { rateTouched.current = true; setRate(v); }} suffix="%" testid="mc-rate" />
        </div>
        {live?.rate30 && (
          <p className="text-[11px] text-slate-500 -mt-1" data-testid="mc-live-rates">
            U.S. average ({live.as_of}):{" "}
            <button type="button" className="font-semibold text-emerald-700 hover:underline" onClick={() => { rateTouched.current = true; setRate(String(live.rate30)); }} data-testid="mc-use-30">30-yr {live.rate30}%</button>
            {" · "}
            <button type="button" className="font-semibold text-emerald-700 hover:underline" onClick={() => { rateTouched.current = true; setRate(String(live.rate15)); }} data-testid="mc-use-15">15-yr {live.rate15}%</button>
          </p>
        )}
        <div className="grid grid-cols-3 gap-2.5">
          <Field label="Tax / yr" value={tax} onChange={setTax} suffix="$" testid="mc-tax" />
          <Field label="Insurance / yr" value={ins} onChange={setIns} suffix="$" testid="mc-ins" />
          <Field label="HOA / mo" value={hoa} onChange={setHoa} suffix="$" testid="mc-hoa" />
        </div>
      </div>
      <div className="space-y-2.5">
        <Stat label="Monthly payment (all-in)" value={fmt2(r.monthly)} big testid="mc-monthly" />
        <Stat label="Principal & interest" value={fmt2(r.pi)} testid="mc-pi" />
        <div className="grid grid-cols-2 gap-2.5">
          <Stat label="Loan amount" value={fmt(r.loan)} testid="mc-loan" />
          <Stat label="Total interest" value={fmt(r.totalInterest)} testid="mc-interest" />
        </div>
        <p className="text-[11px] text-slate-500">
          Over {num(years)} years you'll pay {fmt(r.totalPaid)} in principal & interest
          {num(tax) + num(ins) + num(hoa) > 0 ? ` — plus taxes, insurance & HOA.` : "."}
        </p>
      </div>
    </div>
  );
}

function InvestCalc() {
  const [initial, setInitial] = useState("10000");
  const [monthly, setMonthly] = useState("500");
  const [rate, setRate] = useState("8");
  const [years, setYears] = useState("20");

  const r = useMemo(() => {
    const i = num(rate) / 100 / 12;
    const n = Math.max(1, num(years) * 12);
    const fvInitial = num(initial) * Math.pow(1 + i, n);
    const fvContrib = i > 0 ? num(monthly) * ((Math.pow(1 + i, n) - 1) / i) : num(monthly) * n;
    const fv = fvInitial + fvContrib;
    const contributed = num(initial) + num(monthly) * n;
    const rows = [];
    for (let y = 5; y <= num(years); y += 5) {
      const m = y * 12;
      const v = num(initial) * Math.pow(1 + i, m) + (i > 0 ? num(monthly) * ((Math.pow(1 + i, m) - 1) / i) : num(monthly) * m);
      rows.push({ y, v });
    }
    return { fv, contributed, growth: fv - contributed, rows };
  }, [initial, monthly, rate, years]);

  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <div className="space-y-2.5">
        <Field label="Starting amount" value={initial} onChange={setInitial} suffix="$" testid="ic-initial" />
        <Field label="Monthly contribution" value={monthly} onChange={setMonthly} suffix="$" testid="ic-monthly" />
        <div className="grid grid-cols-2 gap-2.5">
          <Field label="Annual return" value={rate} onChange={setRate} suffix="%" testid="ic-rate" />
          <Field label="Years" value={years} onChange={setYears} suffix="yrs" testid="ic-years" />
        </div>
        {r.rows.length > 0 && (
          <div className="border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-xs">
              <tbody>
                {r.rows.map((row) => (
                  <tr key={row.y} className="border-b border-slate-100 last:border-0">
                    <td className="px-2.5 py-1 text-slate-500">After {row.y} yrs</td>
                    <td className="px-2.5 py-1 text-right font-semibold tabular-nums">{fmt(row.v)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="space-y-2.5">
        <Stat label={`Value in ${num(years)} years`} value={fmt(r.fv)} big testid="ic-fv" />
        <div className="grid grid-cols-2 gap-2.5">
          <Stat label="You put in" value={fmt(r.contributed)} testid="ic-contrib" />
          <Stat label="Growth earned" value={fmt(r.growth)} testid="ic-growth" />
        </div>
        <div className="h-3 rounded-full overflow-hidden bg-slate-100 flex" title="Contributions vs growth">
          <div className="bg-slate-400" style={{ width: `${Math.min(100, (r.contributed / (r.fv || 1)) * 100)}%` }} />
          <div className="bg-emerald-500 flex-1" />
        </div>
        <p className="text-[11px] text-slate-500">
          Grey = money you contributed · Green = compound growth at {num(rate)}%/yr (compounded monthly).
        </p>
      </div>
    </div>
  );
}

/** Desktop app: Mortgage + Investment calculators. */
export default function MoneyCalcApp() {
  const [tab, setTab] = useState("mortgage");
  return (
    <div className="h-full flex flex-col bg-white" data-testid="money-calc-app">
      <div className="flex gap-1 px-3 pt-3">
        <button type="button" onClick={() => setTab("mortgage")}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-t-md text-xs font-bold border border-b-0 ${tab === "mortgage" ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "border-transparent text-slate-500 hover:text-slate-800"}`}
          data-testid="money-tab-mortgage">
          <Home className="w-3.5 h-3.5" /> Mortgage
        </button>
        <button type="button" onClick={() => setTab("invest")}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-t-md text-xs font-bold border border-b-0 ${tab === "invest" ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "border-transparent text-slate-500 hover:text-slate-800"}`}
          data-testid="money-tab-invest">
          <TrendingUp className="w-3.5 h-3.5" /> Investment
        </button>
      </div>
      <div className="flex-1 overflow-y-auto border-t border-slate-200 p-4">
        {tab === "mortgage" ? <MortgageCalc /> : <InvestCalc />}
      </div>
    </div>
  );
}
